import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SurveyPageDto, SurveyExcavatorGroupDto, SurveyYazamReportDto, SurveyPersonDto, SurveyExcavatorDto } from 'src/app/models/survey-page-dto';
import { TranslateLangPipe } from 'src/app/components/shared/pipes/translateLang.pipe';
import { RoutingService } from 'src/app/services/routing.service';
import { SearchFieldsService } from 'src/app/services/search-fields.service';
import { FieldsListsService } from 'src/app/services/fields-lists.service';
import { EntityType } from 'src/app/enums/entity-type.enum';
import { RequestKeys } from 'src/app/enums/request-keys.enum';
import { MultiLangStrDto } from 'src/app/models/multi-lang-str-dto';
import { ImageDto } from 'src/app/models/image-dto';
import { ENTITY_TYPE, ENTITY_ID } from 'src/app/constants';

@Component({
  selector: 'survey-details',
  templateUrl: './survey-details.component.html',
  styleUrls: ['./survey-details.component.scss']
})
export class SurveyDetailsComponent implements OnInit {
  @Input() selectedSurvey!: SurveyPageDto;
  @Input() currentLanguage!: Observable<string>;
  @Input() selectedEntityId: number | null = null;
  @Output() highlightShape = new EventEmitter<{entityId: number, entityType: string}>();
  
  currentLang: string = 'he';
  expandedLicenses: Set<number> = new Set();
  licensesSectionOpen: boolean = true;
  yazamReportsSectionOpen: boolean = true;
  isUnauthorizedDialogOpen: boolean = false;
  processedUnauthorizedLicenses: Array<{ license: string; expedition: string; }> = [];
  unauthorizedLicenseNumbers: Set<string> = new Set();
  surveyImages: ImageDto[] = [];
  surveyImageThumbnails: ImageDto[] = [];

  constructor(
    private translateLang: TranslateLangPipe,
    private routingService: RoutingService,
    private searchFieldsService: SearchFieldsService,
    private fieldsListsService: FieldsListsService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.currentLanguage.subscribe(lang => {
      this.currentLang = lang;
    });
    
    // Process unauthorized licenses
    this.processUnauthorizedLicenses();
    
    // Process images for gallery
    this.processImages();
    
    // Auto-expand single license
    if(this.selectedSurvey?.licenses?.length === 1) {
      this.expandedLicenses.add(0);
    }
  }

  toggleLicense(index: number): void {
    if (this.expandedLicenses.has(index)) {
      this.expandedLicenses.delete(index);
    } else {
      this.expandedLicenses.add(index);
    }
  }

  isLicenseExpanded(index: number): boolean {
    return this.expandedLicenses.has(index);
  }

  toggleLicensesSection(): void {
    this.licensesSectionOpen = !this.licensesSectionOpen;
  }

  toggleYazamReportsSection(): void {
    this.yazamReportsSectionOpen = !this.yazamReportsSectionOpen;
  }

  getTranslatedValue(value: any): string {
    if (typeof value === 'string') {
      return value;
    }
    if (value && typeof value === 'object') {
      return this.currentLang === 'he' ? (value.hebrew || value.english || '') : (value.english || value.hebrew || '');
    }
    return '';
  }

  getSurveyName(): string {
    return this.currentLang === 'he' 
      ? (this.selectedSurvey?.surveyNameHebrew || this.selectedSurvey?.surveyNameEnglish || '')
      : (this.selectedSurvey?.surveyNameEnglish || this.selectedSurvey?.surveyNameHebrew || '');
  }

  getInstitutionName(excavatorGroup: SurveyExcavatorGroupDto): string {
    return this.currentLang === 'he'
      ? (excavatorGroup.institutionHebrew || excavatorGroup.institutionEnglish || '')
      : (excavatorGroup.institutionEnglish || excavatorGroup.institutionHebrew || '');
  }

  getPersonName(person: SurveyPersonDto | SurveyExcavatorDto): string {
    if (!person) return '';
    return this.currentLang === 'he'
      ? (person.nameHebrew || person.nameEnglish || '')
      : (person.nameEnglish || person.nameHebrew || '');
  }

  navigateToEntity(entityId: number, entityType: string): void {
    this.routingService['navigateWithMerge']({ [ENTITY_TYPE]: entityType, [ENTITY_ID]: entityId });
  }

  goTo(personEntityId: number): void {
    // Close entity page and navigate to search results with only persons filter
    // Use navigate without merge to replace all params
    this.routingService['router'].navigate([], { 
      queryParams: {
        persons: personEntityId,
        tabId: 0
      }
    });
  }

  handlePersonClick(person: SurveyPersonDto | SurveyExcavatorDto): void {
    if (person.personEntityId) {
      this.goTo(person.personEntityId);
    } else {
      // Fallback to name search if no personEntityId
      const searchName = this.getPersonName(person);
      if (searchName) {
        this.routingService['navigateWithMerge']({ 
          entityType: undefined,
          entityId: undefined,
          text: searchName
        });
      }
    }
  }

  openUrl(url: string): void {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  onMapIconClick(entityId: number, entityType: string, event: Event): void {
    event.stopPropagation();
    this.highlightShape.emit({ entityId, entityType });
  }

  hasUnauthorizedContent(): boolean {
    return false;
  }

  getUnauthorizedCount(): number {
    return 0;
  }

  processImages(): void {
    // Images not yet implemented for surveys
    this.surveyImages = [];
    this.surveyImageThumbnails = [];
  }

  handleOpenDialog(): void {
    this.isUnauthorizedDialogOpen = true;
  }

  handleCloseDialog(): void {
    this.isUnauthorizedDialogOpen = false;
  }

  processUnauthorizedLicenses(): void {
    // Survey unauthorized licenses handling - simplified for now
    this.processedUnauthorizedLicenses = [];
    this.unauthorizedLicenseNumbers = new Set();
  }

  hasUnauthorizedContentForType(entityType: 'licenses' | 'reports'): boolean {
    if (!this.selectedSurvey || this.unauthorizedLicenseNumbers.size === 0) {
      return false;
    }

    let items: any[] = [];
    switch (entityType) {
      case 'licenses':
        items = this.selectedSurvey.licenses || [];
        return items.some(item => this.unauthorizedLicenseNumbers.has(item.licenseLongName));
      case 'reports':
        items = this.selectedSurvey.yazamReports || [];
        break;
    }

    return items.some(item => this.unauthorizedLicenseNumbers.has(item.licenseNumber));
  }

  getUnauthorizedCountForType(entityType: 'licenses' | 'reports'): number {
    if (!this.selectedSurvey || this.unauthorizedLicenseNumbers.size === 0) {
      return 0;
    }

    let items: any[] = [];
    switch (entityType) {
      case 'licenses':
        items = this.selectedSurvey.licenses || [];
        const matchingLicenses = items.filter(item => this.unauthorizedLicenseNumbers.has(item.licenseLongName));
        return matchingLicenses.length;
      case 'reports':
        items = this.selectedSurvey.yazamReports || [];
        break;
    }

    const matchingItems = items.filter(item => this.unauthorizedLicenseNumbers.has(item.licenseNumber));
    return matchingItems.length;
  }

  getProcessedUnauthorizedLicensesForType(entityType: 'licenses' | 'reports'): Array<{ license: string; expedition: string; }> {
    return [];
  }
}
